import _ from 'lodash';
import Vue from 'vue';
import * as MeitavDashApi from '@/api/MeitavDashApi';
import { BeneficiaryOptions, SAVING_FOR_WHOM_OPTIONS } from '@/constants/meitav-dash/open-fund';

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function _isPiiIdKey(key) {
  return _.endsWith(key, 'PiiId') || _.endsWith(key, 'PiiIds');
}

export default {
  namespaced: true,
  state: {
    firstName: undefined,
    lastName: undefined,
    email: undefined,
    savingForWhat: undefined,
    howToAddressMaleOrFemale: undefined,
    savingForWhom: SAVING_FOR_WHOM_OPTIONS.MYSELF,
    monthlyDepositSum: 200,
    oneTimeDepositSum: undefined,
    idNumber: undefined,
    gender: undefined,
    birthDate: undefined,
    birthCountry: undefined,
    street: undefined,
    houseNumber: undefined,
    apartment: undefined,
    city: undefined,
    isUsaResident: undefined,
    isPayingUsaTax: undefined,
    isOecd: undefined,
    agreeLegalConditions: false,
    beneficiaryChoice: BeneficiaryOptions.MYSELF,
    beneficiaries: [],
    beneficiaryUnknownReason: undefined,
    agreeToReceiveMailFromMeitav: false,
    agreeToTermsAndConditions: false,
    assignPowerOfAttorney: false,
    mailingChannel: undefined,
    signature: undefined,
    bank: undefined,
    bankBranch: undefined,
    accountNumber: undefined,
    bankAccountOwner: undefined,
    phoneNumber: undefined,
    requestId: undefined,
    socialIdType: undefined,
    socialIdFiles: undefined,

    // Ephemeral state
    showUpliftDialog: true,
  },

  mutations: {
    setRequestId(state, requestId) {
      state.requestId = requestId;
    },
    setFirstName(state, firstName) {
      state.firstName = firstName;
    },
    setLastName(state, lastName) {
      state.lastName = lastName;
    },
    setSavingForWhom(state, savingForWhom) {
      state.savingForWhom = savingForWhom;
    },
    setHowToAddressMaleOrFemale(state, howToAddressMaleOrFemale) {
      state.howToAddressMaleOrFemale = howToAddressMaleOrFemale;
    },
    setSavingForWhat(state, savingForWhat) {
      state.savingForWhat = savingForWhat;
    },
    setMonthlyDepositSum(state, monthlyDepositSum) {
      state.monthlyDepositSum = monthlyDepositSum;
    },
    setOneTimeDepositSum(state, oneTimeDepositSum) {
      state.oneTimeDepositSum = oneTimeDepositSum;
    },
    setEmail(state, email) {
      state.email = email;
    },
    setIdNumber(state, socialIdNumber) {
      state.idNumber = socialIdNumber;
    },
    setGender(state, gender) {
      state.gender = gender;
    },
    setBirthDate(state, birthDate) {
      state.birthDate = birthDate;
    },
    setBirthCountry(state, birthCountry) {
      state.birthCountry = birthCountry;
    },
    setCity(state, city) {
      state.city = city;
    },
    setApartment(state, apartment) {
      state.apartment = apartment;
    },
    setHouseNumber(state, houseNumber) {
      state.houseNumber = houseNumber;
    },
    setStreet(state, street) {
      state.street = street;
    },
    setIsUsaResident(state, isUsaResident) {
      state.isUsaResident = isUsaResident;
    },
    setIsPayingUsaTax(state, payingUsaTax) {
      state.isPayingUsaTax = payingUsaTax;
    },
    setIsOecd(state, isOecd) {
      state.isOecd = isOecd;
    },
    setAgreeLegalConditions(state, agreeLegalConditions) {
      state.agreeLegalConditions = agreeLegalConditions;
    },
    setBeneficiaryChoice(state, beneficiaryChoice) {
      state.beneficiaryChoice = beneficiaryChoice;
    },
    setBeneficiaries(state, beneficiaries) {
      state.beneficiaries = beneficiaries;
    },
    setBeneficiary(state, { beneficiary, index }) {
      state.beneficiaries.splice(index, 1, beneficiary);
    },
    setBeneficiaryUnknownReason(state, reason) {
      state.beneficiaryUnknownReason = reason;
    },
    setAgreeToReceiveMailFromMeitav(state, agreeToReceiveMailFromMeitav) {
      state.agreeToReceiveMailFromMeitav = agreeToReceiveMailFromMeitav;
    },
    setAssignPowerOfAttorney(state, assignPowerOfAttorney) {
      state.assignPowerOfAttorney = assignPowerOfAttorney;
    },
    setAgreeToTermsAndConditions(state, agreeToTermsAndConditions) {
      state.agreeToTermsAndConditions = agreeToTermsAndConditions;
    },
    setMailingChannel(state, mailingChannel) {
      state.mailingChannel = mailingChannel;
    },
    setSignature(state, signature) {
      state.signature = signature;
    },
    setBank(state, bank) {
      state.bank = bank;
    },
    setBankBranch(state, bankBranch) {
      state.bankBranch = bankBranch;
    },
    setAccountNumber(state, bankAccountNumber) {
      state.accountNumber = bankAccountNumber;
    },
    setBankAccountOwner(state, bankAccountOwner) {
      state.bankAccountOwner = bankAccountOwner;
    },
    setPhoneNumber(state, phoneNumber) {
      state.phoneNumber = phoneNumber;
    },
    setSocialIdType(state, socialIdType) {
      state.socialIdType = socialIdType;
    },
    addSocialIdPart(state, { partName, fileVersion }) {
      if (_.isUndefined(state.socialIdFiles)) {
        state.socialIdFiles = {};
      }
      Vue.set(state.socialIdFiles, partName, fileVersion);
    },
    removeSocialIdPart(state, { partName }) {
      if (state.socialIdFiles) {
        Vue.set(state.socialIdFiles, partName, undefined);
      }
    },
    setSocialIdFiles(state, socialIdFiles) {
      state.socialIdFiles = socialIdFiles;
    },

    // Ephemeral state
    setUpliftDialogShown(state) {
      state.showUpliftDialog = false;
    },
  },
  actions: {
    async submitRequest({ state }) {
      return await MeitavDashApi.submitOpenFundRequest(state.requestId);
    },
    async save({ state }) {
      await MeitavDashApi.saveWizardState(state.requestId, _.omit(state, '$metadata'));
    },
    async getState({ commit }) {
      const requestId = await MeitavDashApi.getOrCreateOpenFundRequest();
      commit('setRequestId', requestId);
      const wizardState = await MeitavDashApi.getWizardState(requestId);
      _.keys(wizardState).filter(key => !_isPiiIdKey(key)).forEach(key => {
        const value = wizardState[key];
        commit(`set${capitalizeFirstLetter(key)}`, value);
      });
      return wizardState;
    },
    socialIdPartRemoved({ commit }, { socialIdPart }) {
      commit('removeSocialIdPart', socialIdPart);
    },
    setBeneficiaryChoice({ commit }, beneficiaryChoice) {
      commit('setBeneficiaryChoice', beneficiaryChoice);
      switch (beneficiaryChoice) {
        case BeneficiaryOptions.MYSELF: {
          commit('setBeneficiaries', []);
          break;
        }
        case BeneficiaryOptions.UNKNOWN: {
          commit('setBeneficiaries', []);
          break;
        }
        case BeneficiaryOptions.OTHERS: break;
        default: break;
      }
    },
  },
};
